import { EventEmitter } from 'events';

import * as THREE from 'three';
import * as Stats from 'stats.js';

import * as LocationStore from '../../stores/location-store';
import { Zoom } from './controls/Zoom';
import { Earth } from './Earth';
import { Cloud } from './Cloud';
import { Marker } from './marker/Marker';
import { degreeToRadian } from '../shared';
import { Atmosphere } from './Atmosphere';

export class SceneManager extends EventEmitter {
    constructor(view){
        super();
        this.view = view;
        this.destMoveX = 0;
        this.destMoveY = 0;
        this.currentMoveX = 0;
        this.currentMoveY = 0;
        this.targetMoveX = 0;
        this.targetMoveY = 0;
        this.speed = 0.06;
        this.scene = new THREE.Scene();
        this.markers = [];
        this.stopSpin = false;
        this.isActive = true;

       this.vector = new THREE.Vector3();

        this._animate = this._animate.bind(this);

        this._init();
        this._addObjects();
        this._addLights();
        this._addMarkers();
        this._addStat();

        this.animationFrame = requestAnimationFrame(this._animate.bind(this));
    }

    _init() {
        /**
         * Renderer
         * For performance intensive apps, you can also give setSize smaller values, like window.innerWidth/2 and window.innerHeight/2, which will make the app render at half size.
         * If you wish to keep the size of your app but render it at a lower resolution, you can do so by calling setSize with false as updateStyle. For example, setSize(window.innerWidth/2, window.innerHeight/2, false) will render your app at half resolution, given that your <canvas> has 100% width and height.
         * uncomment this.renderer.setPixelRatio( window.devicePixelRatio ); if updateStyle = false
         */
        this.renderer = new THREE.WebGLRenderer({antialias:true, alpha: true});
        // this.renderer.shadowMapEnabled = true;
        // this.renderer.shadowMapSoft = true;

        /* VERY IMPORTANT FOR SHADER*/
        /**
         *  If not setClearColor, the glow will have a black bg
         */
        // this.renderer.setClearColor(0xffffff, 0);

        this.renderer.setSize( window.innerWidth, window.innerHeight);
        /**
         * Sets device pixel ratio. This is usually used for HiDPI device to prevent bluring output canvas.
         */
        // this.renderer.setPixelRatio( window.devicePixelRatio );
        this.view.appendChild( this.renderer.domElement );

        /**
         * Perspective Camera
         * The first attribute is the field of view. FOV is the extent of the scene that is seen on the display at any given moment. The value is in degrees.
         * The second one is the aspect ratio.
         * The next two attributes are the near and far clipping plane. Objects further away from the camera than the value of far or closer than near won't be rendered. Use other values in your apps to get better performance.
         */
        this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1500);
        this.camera.position.set(0, 0, 1.6); // 1 = bigger, 2 = smaller

        /**
         * @type {Raycaster} for on Marker press detection
         */
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();

        /**
         * @type {Zoom}
         */
        this.zoom = new Zoom(this.camera, this.renderer.domElement);
    }

    _addLights() {
        this.scene.add(new THREE.AmbientLight(0x444444));

        // add spotlight for the shadows (remove this if you dont want overexposed)
        // this.spotLight = new THREE.SpotLight(0xffffff, 0.1, 1000, 120, 1, 2.4);
        // this.scene.add(this.spotLight);
        // this.spotLight.position.set(50, 80, -70);

        // function SpotLight( color, intensity, distance, angle, penumbra, decay ) {
        this.spotLight = new THREE.SpotLight(0xffffff, 3, 1024, 1.05, 1, 4);
        this.spotLight.lookAt(this.earth.getMesh().position);
        this.spotLight.position.set(100, 100, 100);
        this.scene.add(this.spotLight);
    }

    _addObjects() {
        this.container = new THREE.Object3D();
        this.earth = new Earth();
        this.cloud = new Cloud();
        this.earth.getMesh().add(this.cloud.getMesh());
        this.container.add(this.earth.getMesh());
        this.scene.add(this.container);

        /**
         * add atmosphere to scene
         */
        this.atmosphere = new Atmosphere(this.camera);
        this.scene.add(this.atmosphere.getMesh());
        this.scene.glow = this.atmosphere.getMesh();
    }

    /**
     * * @private
     * * If more than one content in one place, use the first one for marker
     */
    _addMarkers() {
        const places = LocationStore.get();
        for(const name in places) {
            const place = places[name][0];
            const siblings = places[name];
            const contentId = place.contentId;
            const type = place.type;
            const marker = new Marker(siblings, contentId, parseFloat(place.latitude), parseFloat(place.longitude), this.camera, type);
            marker.testDepth(this.camera, this._distanceBetweenCameraAndGlobe());
            marker.setInitialDepth();
            marker.updateTexture();
            this.container.add(marker);
            this.markers.push(marker);
        }
        /*for(let i = 0; i < places.length; i++) {
            const place = places[i];
            const marker = new Marker(place.id, parseFloat(place.latitude), parseFloat(place.longitude), this.camera);
            marker.testDepth(this.camera, this._distanceBetweenCameraAndGlobe());
            marker.setInitialDepth();
            marker.updateTexture();
            this.container.add(marker);
            this.markers.push(marker);
        }*/
    }

    _animate() {
        this.stats.begin();

        if(this.isActive) {
            this.cloud.move();
            // this.atmosphere.move();
            if(!this.stopSpin) {
                this.container.rotation.y += degreeToRadian(0.03);
            }


            this._spin();

            /**
             * Calc dist betweeb camera and globe for markers' testDepth
             */
            const dist_globeCamera = this._distanceBetweenCameraAndGlobe();
            const zoomLevel = this.zoom.getEyePosition();

            /* marker */
            for(let i = 0; i < this.markers.length; i++) {
                const marker = this.markers[i];
                marker.testDepth(dist_globeCamera);
                marker.updateScale(zoomLevel);
                marker.update();
            }

            this.zoom.update();

            // this.renderer.clear();
            this.renderer.render(this.scene, this.camera);
            this.animationFrame = requestAnimationFrame(this._animate);

        }
        this.stats.end();
    }

    active() {
        this.isActive = true;
        this.animationFrame = requestAnimationFrame(this._animate);
    }

    _spin() {
        this.container.rotation.x -= degreeToRadian(this.targetMoveY);
        this.container.rotation.y -= degreeToRadian(this.targetMoveX);
        // this.container.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), -degreeToRadian(this.targetMoveY));
        // this.container.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), -degreeToRadian(this.targetMoveX));

        this.container.rotation.x = THREE.Math.clamp(this.container.rotation.x, -Math.PI / 2 + degreeToRadian(20), Math.PI / 2 - degreeToRadian(20));


        if(Math.abs(this.targetMoveX) < 0.001) {
            this.targetMoveX = 0;
        }

        if(Math.abs(this.targetMoveY) < 0.001) {
            this.targetMoveY = 0;
        }

        this.targetMoveX = (this.destMoveX - this.currentMoveX) * this.speed;
        this.currentMoveX += this.targetMoveX;

        this.targetMoveY = (this.destMoveY - this.currentMoveY) * this.speed;
        this.currentMoveY += this.targetMoveY;
    }

    setMoveDiff(diffX, diffY) {
        this.destMoveX = diffX;
        this.destMoveY = diffY;
        this.currentMoveX = 0;
        this.currentMoveY = 0;

        this._spin();
    }

    onMarkerPress(event) {
        this.mouse.x = ( event.clientX / this.renderer.domElement.clientWidth ) * 2 - 1;
        this.mouse.y = -( event.clientY / this.renderer.domElement.clientHeight ) * 2 + 1;

        this.raycaster.setFromCamera( this.mouse, this.camera );

        /**
         * Only the markers at front can be clicked
         */
        const intersects = this.raycaster.intersectObjects(
            this.markers.filter( (marker) => {
                if(marker.isFront) return marker;
            }),
            true
        );

        if ( intersects.length > 0 ) {
            this.stopSpin = true;
            this.emit('onMarkerClick', intersects[intersects.length - 1].object.parent, event.clientX, event.clientY);
            this.zoom.scaleUp();
        }
    }

    filterMarker(type) {
        for(let i = 0; i < this.markers.length; i++) {
            if (this.markers[i].type === type) {
                this.markers[i].getChildMesh().scale.set(1, 1, 1);
            } else {
                this.markers[i].getChildMesh().scale.set(0.001, 0.001, 0.001);
            }

            if (type === 'all') {
                this.markers[i].getChildMesh().scale.set(1, 1, 1);
            }
        }

    }

    _addStat() {
        this.stats = new Stats();
        this.stats.dom.className = "stats";
        if(process.env.NODE_ENV !== 'development') this.stats.dom.className += " hide";
        this.stats.showPanel(0);
        document.body.appendChild( this.stats.dom );
    }

    cameraZoomOut() {
        this.zoom.scaleDown();
        this.stopSpin = false;
    }

    _distanceBetweenCameraAndGlobe() {
        const a = this.camera.position;
        const b = this.container.position;
        return a.distanceTo( b );
    }

    _toScreenPosition(obj) {
        const widthHalf = 0.5* this.renderer.domElement.clientWidth;
        const heightHalf = 0.5* this.renderer.domElement.clientHeight;

        console.log(obj.matrixWorld);
        this.container.updateMatrixWorld();
        this.vector.setFromMatrixPosition(obj.matrixWorld);
        this.vector.project(this.camera);

        console.log('vector after transformation:', this.vector );

        this.vector.x = ( this.vector.x * widthHalf ) + widthHalf;
        this.vector.y = - ( this.vector.y * heightHalf ) + heightHalf;

        return {
            x: this.vector.x,
            y: this.vector.y
        };
    }

    resizeScene() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize( window.innerWidth, window.innerHeight );
    }

    touchZoom(dist, direction) {
        this.zoom.touching(dist, direction);
    }

    getAnimationFrame() {
        return this.animationFrame;
    }

}
